import React, { useState } from "react";
import { DesignField } from "@/components/DesignField";
import { Wordmark } from "@/components/UIBits";
import { T } from "@/lib/copy";
import { X, ChevronLeft, ChevronRight, Eye, Target, Flame, Zap } from "lucide-react";

function SlideVisual({ i, lang }) {
  const t = T(lang);
  if (i === 0) {
    return <div className="w-full" style={{ maxWidth: 300 }}><DesignField field="STD" /></div>;
  }
  if (i === 1) {
    return (
      <div className="w-full flex flex-col items-center gap-3" style={{ maxWidth: 300 }}>
        <div className="ff-timer" style={{ position: "relative", width: "70%" }}><div className="ff-timer-fill" style={{ width: "62%" }} /></div>
        <div className="grid grid-cols-2 gap-2 w-full">
          <div className="panel-2 p-3 text-center font-score italic" style={{ color: "var(--sm-blaze)", boxShadow: "var(--sm-glow-hot)" }}><Eye size={18} style={{ margin: "0 auto 4px" }} />Go big</div>
          <div className="panel-2 p-3 text-center font-score italic" style={{ color: "rgba(244,239,230,0.85)" }}>Play safe</div>
        </div>
      </div>
    );
  }
  if (i === 2) {
    return (
      <div className="w-full flex flex-col items-center gap-3" style={{ maxWidth: 300 }}>
        <div className="panel p-4 w-full flex items-center justify-between">
          <div className="font-score italic tnum" style={{ fontSize: 30, color: "var(--sm-chalk)" }}>13<span style={{ color: "rgba(244,239,230,0.5)", fontSize: 20 }}>/0</span></div>
          <Target size={26} style={{ color: "var(--sm-blaze)" }} />
          <div className="font-mono text-[11px] up tracking-[0.14em]" style={{ color: "var(--sm-blaze)" }}>TARGET 14</div>
        </div>
        <div className="inline-flex items-center gap-2 font-mono text-[10px] up tracking-[0.14em]" style={{ color: "var(--sm-steel-2)" }}><Zap size={12} /> Level it &rarr; Eliminator</div>
      </div>
    );
  }
  return (
    <div className="w-full flex flex-col items-center gap-4" style={{ maxWidth: 300 }}>
      <Flame size={40} style={{ color: "var(--sm-blaze)" }} />
      <div className="flex items-center justify-center gap-2">
        {Array.from({ length: 7 }, (_, k) => (
          <span key={k} className={"streak-dot" + (k < 4 ? " on" : "") + (k === 3 ? " today" : "")} style={k < 4 ? { background: "var(--sm-blaze)" } : {}} />
        ))}
      </div>
    </div>
  );
}

export default function HowToPlay({ lang, onClose }) {
  const t = T(lang);
  const slides = t.howto || [];
  const [i, setI] = useState(0);
  const last = i >= slides.length - 1;
  const ICONS = [Eye, Eye, Target, Flame];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "radial-gradient(120% 80% at 50% 0%, rgba(20,30,52,0.99), rgba(7,12,24,0.995))", backdropFilter: "blur(10px)" }} data-testid="howto-overlay">
      <div className="relative w-full flex flex-col" style={{ maxWidth: 380, minHeight: 520 }}>
        <div className="flex items-center justify-between">
          <Wordmark size={20} />
          <button className="back-chip" onClick={onClose} data-testid="howto-skip-button">{t.skip} <X size={14} /></button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center text-center gap-6 py-6" data-testid={`howto-slide-${i}`}>
          <div className="anim-fade" key={"v" + i}>{<SlideVisual i={i} lang={lang} />}</div>
          <div className="anim-rise" key={"t" + i}>
            <div className="font-mono text-[10px] up tracking-[0.2em] mb-2" style={{ color: "var(--sm-blaze)" }}>{i + 1} / {slides.length}</div>
            <h2 className="font-score italic up" style={{ fontSize: 26, color: "var(--sm-chalk)" }}>{slides[i] && slides[i].t}</h2>
            <p className="font-reading text-[15px] leading-relaxed mt-2" style={{ color: "rgba(244,239,230,0.82)", maxWidth: 320 }}>{slides[i] && slides[i].b}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3 justify-center">
          {slides.map((_, k) => (
            <span key={k} className="dot" style={{ background: k === i ? "var(--sm-blaze)" : "rgba(244,239,230,0.18)", width: k === i ? 22 : 8, height: 8, borderRadius: 999, transition: "width .25s var(--sm-ease-out), background .25s var(--sm-ease-out)" }} />
          ))}
        </div>

        <div className="flex items-center gap-3">
          {i > 0 && (
            <button className="btn-secondary px-5 py-3.5 flex items-center justify-center" onClick={() => setI((v) => v - 1)} data-testid="howto-back-button" aria-label="Back"><ChevronLeft size={18} /></button>
          )}
          {last ? (
            <button className="btn-primary flex-1 py-4 text-lg" onClick={onClose} data-testid="howto-start-button">{t.gotItPlay}</button>
          ) : (
            <button className="btn-primary flex-1 py-4 text-lg flex items-center justify-center gap-2" onClick={() => setI((v) => v + 1)} data-testid="howto-next-button">{t.next} <ChevronRight size={18} /></button>
          )}
        </div>
      </div>
    </div>
  );
}
